import type { SkillKey } from "./types";

/**
 * Strategy lessons, grouped into realms. Each has steps, worked examples (checked by the engine tests), when
 * to reach for it, the mistake to watch for, and which kinds of miss it helps with (`fixes`) so the coach can
 * recommend the right one. `level` is where the strategy starts to pay off and the level its practice drill uses.
 * Add a lesson here and it appears on the Grow tab and at /learn/<key>.
 */
export type MissType = "place" | "sign" | "transpose" | "carry" | "close" | "operation" | "skip" | "other";

export interface Lesson {
  key: string;
  skill: SkillKey | "general";
  title: string;
  level: number;
  blurb: string;
  steps: string[];
  examples: { q: string; answer: number; walk: string }[];
  /** When to reach for this strategy. */
  when: string;
  /** The mistake people make with it. */
  watch: string;
  tip: string;
  /** Kinds of miss this strategy helps prevent. */
  fixes?: MissType[];
}

export interface Realm { key: string; name: string; blurb: string; skills: (SkillKey | "general")[] }
export const REALMS: Realm[] = [
  { key: "addsub", name: "Adding and subtracting", blurb: "Build and break numbers in friendly pieces.", skills: ["add", "sub"] },
  { key: "muldiv", name: "Multiplying and dividing", blurb: "Split, double, halve and chunk.", skills: ["mul", "div"] },
  { key: "percent", name: "Percents", blurb: "Tips, sales and changes from a few building blocks.", skills: ["pct"] },
  { key: "powers", name: "Squares and roots", blurb: "Patterns that make big squares easy.", skills: ["sq", "sqrt"] },
  { key: "units", name: "Units and conversions", blurb: "Good-enough answers fast.", skills: ["unit"] },
  { key: "multi", name: "Multi-step problems", blurb: "Keep your place when there are several steps.", skills: ["multi"] },
  { key: "habits", name: "Habits and checking", blurb: "How to practice, and how to know you're right.", skills: ["general"] },
];

export const LESSONS: Lesson[] = [
  // ---- Adding ----
  {
    key: "add-left-to-right", skill: "add", title: "Add left to right", level: 2,
    blurb: "Build the answer one place at a time, the way you say it.",
    steps: ["Start with the bigger number.", "Add the tens of the other number, then the ones.", "Say each running total out loud or in your head."],
    examples: [
      { q: "47 + 38", answer: 85, walk: "47 + 30 = 77, then 77 + 8 = 85." },
      { q: "256 + 178", answer: 434, walk: "256 + 100 = 356, + 70 = 426, + 8 = 434." },
    ],
    when: "Any sum bigger than a times table.",
    watch: "Losing track of what you've already added. Say the running total each time.",
    tip: "Written addition goes right to left. Mental addition is easier left to right, because you hear the answer in order.",
    fixes: ["carry", "transpose"],
  },
  {
    key: "add-bridge-ten", skill: "add", title: "Bridge through ten", level: 3,
    blurb: "Make the next ten, then add what's left.",
    steps: ["See how far the first number is from the next ten.", "Take that much from the second number to land on the ten.", "Add what remains."],
    examples: [
      { q: "58 + 7", answer: 65, walk: "58 needs 2 to reach 60. 7 − 2 = 5. 60 + 5 = 65." },
      { q: "86 + 27", answer: 113, walk: "86 needs 4 to reach 90. 27 − 4 = 23. 90 + 23 = 113." },
    ],
    when: "When the ones digits add past ten (the carry).",
    watch: "Forgetting to take the borrowed part off the second number.",
    tip: "Landing on a round number is the single most useful habit in mental math.",
    fixes: ["carry"],
  },
  {
    key: "add-round-adjust", skill: "add", title: "Round, then adjust", level: 4,
    blurb: "Add the round number, then fix the difference.",
    steps: ["Spot a number just under a round number (298, 99, 4,995).", "Add the round number instead.", "Take off the amount you rounded up."],
    examples: [
      { q: "299 + 478", answer: 777, walk: "300 + 478 = 778, minus the 1 you added too much = 777." },
      { q: "398 + 245", answer: 643, walk: "400 + 245 = 645, minus 2 = 643." },
    ],
    when: "One number ends in 8 or 9, or sits just under a round number.",
    watch: "Adjusting the wrong way. If you added too much, take it off.",
    tip: "The same idea works for subtraction: take away the round number, then add back.",
    fixes: ["close", "carry"],
  },
  {
    key: "add-group-tens", skill: "add", title: "Group friendly numbers", level: 6,
    blurb: "In a long sum, pair numbers that make round totals first.",
    steps: ["Scan the list for pairs that add to a ten or a hundred.", "Add each pair.", "Add the pair totals."],
    examples: [
      { q: "36 + 48 + 14 + 22", answer: 120, walk: "36 + 14 = 50 and 48 + 22 = 70, so 120." },
      { q: "125 + 380 + 75 + 20", answer: 600, walk: "125 + 75 = 200 and 380 + 20 = 400, so 600." },
    ],
    when: "Three or more numbers.",
    watch: "Skipping a number. Cross off each one as you pair it.",
    tip: "Order doesn't change a sum, so use the order that makes your life easy.",
    fixes: ["carry"],
  },

  // ---- Subtracting ----
  {
    key: "sub-round-up", skill: "sub", title: "Subtract by rounding", level: 3,
    blurb: "Take away a round number, then fix the difference.",
    steps: ["Round the number you subtract up to the next ten (or hundred).", "Subtract the round number.", "Add back what you over-subtracted."],
    examples: [
      { q: "83 − 47", answer: 36, walk: "Take 50: 83 − 50 = 33. You took 3 too many, so add it back: 36." },
      { q: "612 − 285", answer: 327, walk: "Take 300: 612 − 300 = 312. 15 too many, add back: 327." },
    ],
    when: "The number you subtract has an awkward ones digit (7, 8, 9).",
    watch: "Adding back in the wrong direction. You subtracted too much, so the answer goes up.",
    tip: "Checking by adding the answer back to the small number is a quick way to catch slips.",
    fixes: ["carry", "close"],
  },
  {
    key: "sub-count-up", skill: "sub", title: "Count up to subtract", level: 4,
    blurb: "Find the gap by stepping from the small number to the big one.",
    steps: ["Start at the number you're subtracting.", "Step to the next round number, then up by hundreds or tens, then to the target.", "Add up the steps."],
    examples: [
      { q: "1000 − 638", answer: 362, walk: "638 to 700 is 62, 700 to 1000 is 300. 62 + 300 = 362." },
      { q: "503 − 296", answer: 207, walk: "296 to 300 is 4, 300 to 500 is 200, 500 to 503 is 3. 4 + 200 + 3 = 207." },
    ],
    when: "The two numbers are close, or you're making change.",
    watch: "Counting from the wrong end. The answer is the total of your steps.",
    tip: "This is how a cashier counts change back to you.",
    fixes: ["carry", "sign"],
  },
  {
    key: "sub-same-shift", skill: "sub", title: "Shift both numbers", level: 5,
    blurb: "Add the same amount to both numbers and the difference stays put.",
    steps: ["Pick an amount that makes the smaller number round.", "Add it to both numbers.", "Subtract the easy pair."],
    examples: [
      { q: "82 − 47", answer: 35, walk: "Add 3 to both: 85 − 50 = 35." },
      { q: "143 − 68", answer: 75, walk: "Add 2 to both: 145 − 70 = 75." },
    ],
    when: "The smaller number is just under a round number.",
    watch: "Shifting only one of the two numbers.",
    tip: "The gap between two numbers doesn't change when you slide both the same distance.",
    fixes: ["carry", "close"],
  },

  // ---- Multiplying ----
  {
    key: "mul-table-patterns", skill: "mul", title: "Times table patterns", level: 2,
    blurb: "Shortcuts for the tables you find slippery.",
    steps: ["9s: multiply by 10, then subtract the number.", "5s: half of the number times 10.", "4s: double, then double again."],
    examples: [
      { q: "9 × 7", answer: 63, walk: "7 × 10 = 70, minus 7 = 63." },
      { q: "5 × 8", answer: 40, walk: "Half of 8 is 4, times 10 = 40." },
      { q: "4 × 7", answer: 28, walk: "Double 7 = 14, double again = 28." },
    ],
    when: "A table fact you don't know cold.",
    watch: "Mixing up the table you're in. Name it first: \"this is a 9s fact\".",
    tip: "Learn the tables you know least first. Drill those, not all of them.",
    fixes: ["close", "operation"],
  },
  {
    key: "mul-five-twentyfive", skill: "mul", title: "Times 5 and times 25", level: 3,
    blurb: "Multiply by 10 or 100, then halve or quarter.",
    steps: ["× 5 is × 10, then half.", "× 25 is × 100, then a quarter (halve twice)."],
    examples: [
      { q: "64 × 5", answer: 320, walk: "64 × 10 = 640, half is 320." },
      { q: "36 × 25", answer: 900, walk: "36 × 100 = 3,600, a quarter of that is 900." },
    ],
    when: "One of the numbers is 5, 25 or 50.",
    watch: "Halving when you needed a quarter for 25.",
    tip: "Money helps: 25 is a quarter of a dollar.",
    fixes: ["place", "close"],
  },
  {
    key: "mul-split", skill: "mul", title: "Split the smaller number", level: 4,
    blurb: "Break one factor into tens and ones, multiply each, add.",
    steps: ["Pick the smaller number and split it by place (47 is 40 and 7).", "Multiply each piece by the other number.", "Add the two products."],
    examples: [
      { q: "47 × 6", answer: 282, walk: "40 × 6 = 240, 7 × 6 = 42, 240 + 42 = 282." },
      { q: "83 × 7", answer: 581, walk: "80 × 7 = 560, 3 × 7 = 21, 560 + 21 = 581." },
    ],
    when: "A two-digit number times a single digit, or any multiplication you can break into pieces.",
    watch: "Forgetting to add the second piece. Say \"and\" after the first product.",
    tip: "Do the big piece first. Then your running total is already close.",
    fixes: ["carry", "close", "operation"],
  },
  {
    key: "mul-eleven-teens", skill: "mul", title: "Times 11 and the teens", level: 5,
    blurb: "Two quick tricks that look like magic.",
    steps: ["× 11: multiply by 10 and add the number once more.", "Teens × teens: add one number to the other's ones digit, put a zero on the end, then add the product of the ones digits."],
    examples: [
      { q: "54 × 11", answer: 594, walk: "540 + 54 = 594." },
      { q: "13 × 17", answer: 221, walk: "13 + 7 = 20, so 200. Ones: 3 × 7 = 21. 200 + 21 = 221." },
    ],
    when: "Either number is 11, or both are 11 to 19.",
    watch: "Dropping the carry in the middle digit of × 11.",
    tip: "Both tricks are just the split method in disguise.",
    fixes: ["carry"],
  },
  {
    key: "mul-double-half", skill: "mul", title: "Double and halve", level: 6,
    blurb: "Trade a factor of two from one number to the other.",
    steps: ["Halve one number that's even.", "Double the other.", "The product is unchanged, but the numbers are friendlier."],
    examples: [
      { q: "35 × 8", answer: 280, walk: "Halve 8 to 4 and double 35 to 70. 70 × 4 = 280." },
      { q: "16 × 25", answer: 400, walk: "Halve 16 twice to 4, double 25 twice to 100. 4 × 100 = 400." },
    ],
    when: "One number is even and the other is awkward.",
    watch: "Halving one number but forgetting to double the other.",
    tip: "Keep going while both steps stay easy.",
    fixes: ["operation", "place"],
  },
  {
    key: "mul-near-round", skill: "mul", title: "Multiply near a round number", level: 7,
    blurb: "Use 99, 101, 49 or 51 as a round number plus or minus a bit.",
    steps: ["Rewrite the awkward factor as a round number ± a little.", "Multiply by the round number.", "Add or subtract the leftover times the other number."],
    examples: [
      { q: "47 × 99", answer: 4653, walk: "47 × 100 = 4,700, minus 47 = 4,653." },
      { q: "48 × 51", answer: 2448, walk: "48 × 50 = 2,400, plus 48 = 2,448." },
    ],
    when: "A factor is within 1 or 2 of a round number.",
    watch: "Taking the wrong leftover. It's one extra (or one fewer) copy of the other number.",
    tip: "Percent shortcuts rely on exactly this.",
    fixes: ["place", "close"],
  },

  // ---- Dividing ----
  {
    key: "div-chunks", skill: "div", title: "Divide in chunks", level: 3,
    blurb: "Take the easy part off first, then divide what's left.",
    steps: ["Find the largest chunk you can divide in your head.", "Divide it, and keep what's left.", "Divide the rest and add the pieces."],
    examples: [
      { q: "84 ÷ 4", answer: 21, walk: "80 ÷ 4 = 20, 4 ÷ 4 = 1, so 21." },
      { q: "252 ÷ 6", answer: 42, walk: "240 ÷ 6 = 40, 12 ÷ 6 = 2, so 42." },
    ],
    when: "A bigger number divided by a single digit.",
    watch: "Losing the leftover. Write it down in your head before dividing the chunk.",
    tip: "Multiplying back is the check: 21 × 4 = 84.",
    fixes: ["carry", "operation"],
  },
  {
    key: "div-by-five-twentyfive", skill: "div", title: "Divide by 5 and 25", level: 4,
    blurb: "Multiply instead, then move the decimal.",
    steps: ["÷ 5 is × 2, then ÷ 10.", "÷ 25 is × 4, then ÷ 100."],
    examples: [
      { q: "235 ÷ 5", answer: 47, walk: "Double it: 470, then ÷ 10 = 47." },
      { q: "350 ÷ 25", answer: 14, walk: "× 4 is 1,400, then ÷ 100 = 14." },
    ],
    when: "The divisor is 5, 25 or 50.",
    watch: "Moving the decimal the wrong way. Dividing makes the answer smaller.",
    tip: "It's the mirror image of times 5 and times 25.",
    fixes: ["place"],
  },
  {
    key: "div-break-divisor", skill: "div", title: "Divide in two steps", level: 5,
    blurb: "Split a hard divisor into two easy ones.",
    steps: ["Factor the divisor (6 is 2 × 3, 12 is 3 × 4).", "Divide by one factor.", "Divide the result by the other."],
    examples: [
      { q: "168 ÷ 6", answer: 28, walk: "÷ 2 = 84, ÷ 3 = 28." },
      { q: "144 ÷ 12", answer: 12, walk: "÷ 3 = 48, ÷ 4 = 12." },
    ],
    when: "The divisor is 6, 8, 12, 15 or 18.",
    watch: "Dividing by the same factor twice.",
    tip: "Dividing by 8 is halving three times.",
    fixes: ["operation", "carry"],
  },

  // ---- Percents ----
  {
    key: "pct-blocks", skill: "pct", title: "Percents from 10% and 1%", level: 2,
    blurb: "Build any percent out of easy pieces.",
    steps: ["10% is the number with the decimal moved one place left.", "5% is half of that. 1% moves two places.", "Add the pieces."],
    examples: [
      { q: "15% of 80", answer: 12, walk: "10% = 8, 5% = 4, total 12." },
      { q: "35% of 60", answer: 21, walk: "10% = 6, so 30% = 18. 5% = 3. 18 + 3 = 21." },
    ],
    when: "Tips, sales and tax.",
    watch: "Moving the decimal two places for 10%. It's one.",
    tip: "Tips and sales tax are all built this way.",
    fixes: ["place", "close"],
  },
  {
    key: "pct-fractions", skill: "pct", title: "Quarters, halves and eighths", level: 2,
    blurb: "Some percents are just fractions in disguise.",
    steps: ["50% is half. 25% is a quarter. 75% is three quarters.", "12.5% is an eighth: halve three times."],
    examples: [
      { q: "75% of 48", answer: 36, walk: "A quarter of 48 is 12. Three quarters is 36." },
      { q: "12.5% of 64", answer: 8, walk: "Half is 32, half again 16, half again 8." },
    ],
    when: "The percent is 25, 50, 75 or 12.5.",
    watch: "Taking a quarter when the percent is 75.",
    tip: "Learn 25, 50, 75 and 12.5. They come up constantly.",
    fixes: ["operation", "place"],
  },
  {
    key: "pct-flip", skill: "pct", title: "Flip the percent", level: 4,
    blurb: "x% of y is the same as y% of x.",
    steps: ["Look at both numbers.", "If swapping them makes it easier, swap.", "Work it out the easy way round."],
    examples: [
      { q: "8% of 50", answer: 4, walk: "Flip it: 50% of 8 is half of 8 = 4." },
      { q: "36% of 25", answer: 9, walk: "Flip it: 25% of 36 is a quarter of 36 = 9." },
    ],
    when: "A small percent of a friendly number.",
    watch: "Flipping something that isn't a plain \"percent of\".",
    tip: "It works because both are just multiplication.",
    fixes: ["place", "operation"],
  },
  {
    key: "pct-what", skill: "pct", title: "What percent is it?", level: 6,
    blurb: "Scale the whole up to 100 and read off the part.",
    steps: ["Find what to multiply the whole by to reach 100.", "Multiply the part by the same amount.", "That's the percent."],
    examples: [
      { q: "30 is what % of 120", answer: 25, walk: "30 is a quarter of 120 (4 × 30 = 120), so it's 25%." },
      { q: "9 is what % of 20", answer: 45, walk: "20 × 5 = 100, so 9 × 5 = 45%." },
    ],
    when: "\"x is what percent of y\" questions.",
    watch: "Dividing the wrong way round. The part goes on top.",
    tip: "Nice wholes: 20, 25, 50 and 200 scale to 100 in one step.",
    fixes: ["operation", "place"],
  },
  {
    key: "pct-change", skill: "pct", title: "Percent change", level: 7,
    blurb: "Change divided by the starting value.",
    steps: ["Find the change: new minus old.", "Divide it by the old value.", "Turn it into a percent. Decrease means negative."],
    examples: [
      { q: "80 → 100, % change?", answer: 25, walk: "Change is 20. 20 ÷ 80 = a quarter = 25%." },
      { q: "50 → 40, % change?", answer: -20, walk: "Change is −10. −10 ÷ 50 = −20%." },
    ],
    when: "Prices, scores and anything that went up or down.",
    watch: "Dividing by the new value instead of the old.",
    tip: "Up 50% then down 50% does not get you back to the start.",
    fixes: ["operation", "sign"],
  },

  // ---- Squares ----
  {
    key: "sq-neighbors", skill: "sq", title: "Use the square next door", level: 3,
    blurb: "A square is the previous square plus a little.",
    steps: ["Start from a square you know.", "(n + 1)² = n² + 2n + 1.", "Add 2n + 1 to the square you know."],
    examples: [
      { q: "13²", answer: 169, walk: "12² = 144, plus 2 × 12 + 1 = 25, gives 169." },
      { q: "21²", answer: 441, walk: "20² = 400, plus 2 × 20 + 1 = 41, gives 441." },
    ],
    when: "The number is one more (or less) than one you know.",
    watch: "Adding n instead of 2n + 1.",
    tip: "Squares grow by the next odd number: 1, 4, 9, 16 add 3, 5, 7.",
    fixes: ["close"],
  },
  {
    key: "sq-ending-five", skill: "sq", title: "Squares that end in 5", level: 3,
    blurb: "A pattern that always works.",
    steps: ["Take the digits before the 5. Multiply that number by the next number up.", "Write 25 after it."],
    examples: [
      { q: "35²", answer: 1225, walk: "3 × 4 = 12, then 25: 1225." },
      { q: "85²", answer: 7225, walk: "8 × 9 = 72, then 25: 7225." },
    ],
    when: "Any number that ends in 5.",
    watch: "Squaring the first digit instead of multiplying by the next one.",
    tip: "Once you know this one, a whole row of squares is free.",
    fixes: ["operation", "place"],
  },
  {
    key: "sq-near-ten", skill: "sq", title: "Squares near a round number", level: 4,
    blurb: "Step to the nearest ten and back.",
    steps: ["Find how far the number is from the nearest ten (call it d).", "Multiply (number − d) by (number + d). That lands on easy numbers.", "Add d squared."],
    examples: [
      { q: "47²", answer: 2209, walk: "d = 3. 44 × 50 = 2200. Add 3² = 9: 2209." },
      { q: "62²", answer: 3844, walk: "d = 2. 60 × 64 = 3840. Add 2² = 4: 3844." },
    ],
    when: "Any two-digit number not ending in 5 or 0.",
    watch: "Forgetting to add d². Your answer will be a little low.",
    tip: "It works for any number: (n − d)(n + d) = n² − d².",
    fixes: ["close", "carry"],
  },
  {
    key: "sq-hundreds", skill: "sq", title: "Squares just over 100", level: 7,
    blurb: "(100 + r)² splits into three easy parts.",
    steps: ["Write the number as 100 + r.", "10,000 + 200 × r + r².", "Add the three parts."],
    examples: [
      { q: "103²", answer: 10609, walk: "10,000 + 600 + 9 = 10,609." },
      { q: "108²", answer: 11664, walk: "10,000 + 1,600 + 64 = 11,664." },
    ],
    when: "A number in the 100s close to 100.",
    watch: "Using 100 × r instead of 200 × r for the middle part.",
    tip: "The same shape works for (50 + r)² and (200 + r)².",
    fixes: ["place", "carry"],
  },

  // ---- Roots ----
  {
    key: "sqrt-ends", skill: "sqrt", title: "Square roots from the ends", level: 3,
    blurb: "Use the first digits and the last digit to pin the root.",
    steps: ["Ignore the last two digits. The biggest square that fits in the rest gives the tens digit.", "The last digit of the number tells you the last digit of the root (ends in 9: root ends in 3 or 7).", "Compare to the square of the number halfway to pick between two choices."],
    examples: [
      { q: "√1369", answer: 37, walk: "13 holds 9 = 3², so the root starts with 3. It ends in 9, so 33 or 37. 35² = 1225 is lower than 1369, so 37." },
    ],
    when: "Perfect squares up to a few thousand.",
    watch: "Mixing up the two last-digit choices. Test the halfway square.",
    tip: "Knowing the squares up to 25 makes this almost automatic.",
    fixes: ["close", "operation"],
  },
  {
    key: "sqrt-estimate", skill: "sqrt", title: "Estimate a square root", level: 5,
    blurb: "Nearest square, plus a correction.",
    steps: ["Find the closest perfect square r².", "Take how far the number is from r², divide by 2r.", "Add that to r."],
    examples: [
      { q: "√70", answer: 8.4, walk: "Nearest square is 64 (8²). 6 more, ÷ 16 is about 0.4. So about 8.4." },
      { q: "√50", answer: 7.1, walk: "Nearest square is 49 (7²). 1 more, ÷ 14 is about 0.07. So about 7.1." },
    ],
    when: "Not a perfect square and a decimal answer is fine.",
    watch: "Using a square that is further away than the closest one.",
    tip: "The closer the nearest square, the better the estimate.",
    fixes: ["close"],
  },

  // ---- Units ----
  {
    key: "unit-thumb", skill: "unit", title: "Rules of thumb for conversions", level: 1,
    blurb: "Good-enough answers fast.",
    steps: ["km to miles: multiply by 5, divide by 8.", "kg to pounds: double it and add 10%.", "°C to °F: double, subtract 10%, add 32."],
    examples: [
      { q: "80 km → mi", answer: 50, walk: "80 × 5 = 400, ÷ 8 = 50. Exact is 49.7." },
      { q: "50 kg → lb", answer: 110, walk: "Double = 100, plus 10% = 110. Exact is 110.2." },
      { q: "20 °C → °F", answer: 68, walk: "Double = 40, minus 10% = 36, plus 32 = 68." },
    ],
    when: "Everyday conversions where close is fine.",
    watch: "Going the wrong direction. Miles are bigger than kilometers, so there are fewer of them.",
    tip: "The app accepts answers within a few percent. Close is the point.",
    fixes: ["operation", "place"],
  },
  {
    key: "unit-temperature-back", skill: "unit", title: "Fahrenheit to Celsius", level: 3,
    blurb: "The reverse trick: subtract, halve, add a tenth.",
    steps: ["Subtract 32.", "Halve it.", "Add 10% of that."],
    examples: [
      { q: "68 °F → °C", answer: 20, walk: "68 − 32 = 36, half is 18, plus 10% (1.8) is 19.8, about 20." },
      { q: "50 °F → °C", answer: 10, walk: "50 − 32 = 18, half is 9, plus 0.9 is 9.9, about 10." },
    ],
    when: "Weather abroad, ovens and thermometers.",
    watch: "Forgetting to subtract 32 first.",
    tip: "Freezing is 32°F = 0°C. Body temperature is 98.6°F = 37°C.",
    fixes: ["operation", "sign"],
  },
  {
    key: "unit-length-volume", skill: "unit", title: "Inches, centimeters and gallons", level: 3,
    blurb: "Two or three numbers cover most everyday needs.",
    steps: ["Inches to centimeters: × 2.5 (a hair more).", "Centimeters to inches: × 0.4.", "Gallons to liters: × 4, then subtract 5%."],
    examples: [
      { q: "12 in → cm", answer: 30, walk: "12 × 2.5 = 30. Exact is 30.5." },
      { q: "5 gal → L", answer: 19, walk: "5 × 4 = 20, minus 5% (1) = 19. Exact is 18.9." },
    ],
    when: "Lengths, recipes, fuel.",
    watch: "Using × 2.5 in the wrong direction. Centimeters are smaller, so there are more of them.",
    tip: "Say \"inches to centimeters gets bigger\" before you start.",
    fixes: ["operation", "place"],
  },

  // ---- Multi-step ----
  {
    key: "multi-order", skill: "multi", title: "Order of operations", level: 1,
    blurb: "Brackets, then multiply and divide, then add and subtract.",
    steps: ["Do anything in brackets first.", "Then multiply and divide, left to right.", "Then add and subtract, left to right."],
    examples: [
      { q: "3 + 4 × 5", answer: 23, walk: "Multiply first: 4 × 5 = 20. Then 3 + 20 = 23." },
      { q: "(3 + 4) × 5", answer: 35, walk: "Brackets first: 7. Then 7 × 5 = 35." },
    ],
    when: "Anything with more than one operation.",
    watch: "Working strictly left to right.",
    tip: "Say the first step out loud before you do anything else.",
    fixes: ["operation", "sign"],
  },
  {
    key: "multi-hold", skill: "multi", title: "Hold one result at a time", level: 4,
    blurb: "Finish a piece, say its value, then move on.",
    steps: ["Pick the first piece to evaluate.", "Say its value as a single number.", "Replace the piece with that number and continue."],
    examples: [
      { q: "(23 + 17) × 4", answer: 160, walk: "Bracket first: 40. Then 40 × 4 = 160." },
      { q: "12 × 5 + 8 × 5", answer: 100, walk: "12 × 5 = 60, 8 × 5 = 40, together 100." },
    ],
    when: "More than two numbers.",
    watch: "Trying to hold two unfinished pieces at once.",
    tip: "Working memory holds about four things. Replacing pieces with one number frees up room.",
    fixes: ["operation", "carry"],
  },
  {
    key: "multi-factor-out", skill: "multi", title: "Factor out the common number", level: 6,
    blurb: "If two terms share a factor, add or subtract first, then multiply once.",
    steps: ["Spot the factor that appears in both terms.", "Add or subtract the other numbers.", "Multiply once."],
    examples: [
      { q: "17 × 6 + 13 × 6", answer: 180, walk: "17 + 13 = 30, then 30 × 6 = 180." },
      { q: "45 × 8 − 25 × 8", answer: 160, walk: "45 − 25 = 20, then 20 × 8 = 160." },
    ],
    when: "Two products that share a number.",
    watch: "Factoring when the shared number isn't actually shared.",
    tip: "It turns two multiplications into one.",
    fixes: ["operation", "close"],
  },

  // ---- Habits and checking ----
  {
    key: "train-smart", skill: "general", title: "How to practice", level: 1,
    blurb: "Why short, steady practice beats cramming.",
    steps: [
      "Aim for accuracy first. Speed shows up on its own once the steps are automatic.",
      "Ten focused minutes a day beats an hour once a week. Your streak is there to help.",
      "Your working memory holds only a few things at once. Say or picture each partial result before moving on, rather than trying to hold everything.",
      "Use hints freely while a skill is new. A hint costs points, not pride, and missed problems come back later so they stick.",
      "When a level feels easy, the app moves you up. When it feels hard, stay and repeat; that is the learning.",
    ],
    examples: [],
    when: "Always.",
    watch: "Chasing speed before the method is solid.",
    tip: "If you only do one thing: show up every day, even for five problems.",
    fixes: ["skip"],
  },
  {
    key: "check-estimate", skill: "general", title: "Estimate first", level: 2,
    blurb: "Know roughly how big the answer should be before you work it out.",
    steps: ["Round each number to something easy.", "Do the rough calculation.", "Compare your exact answer. It should be close; if it's ten times off, something slipped."],
    examples: [
      { q: "48 × 21", answer: 1008, walk: "Round: 50 × 20 = 1,000. So expect about 1,000. 1,008 fits; 108 or 10,080 would not." },
      { q: "398 + 247", answer: 645, walk: "Round: 400 + 250 = 650. 645 is right in the neighborhood." },
    ],
    when: "Before and after every problem.",
    watch: "Skipping the estimate because you're sure.",
    tip: "This catches nearly every place-value slip.",
    fixes: ["place", "close", "sign"],
  },
  {
    key: "check-last-digit", skill: "general", title: "Check the last digit", level: 3,
    blurb: "A ten-second test that catches arithmetic slips.",
    steps: ["Work out only the last digit of the answer from the last digits of the numbers.", "Compare it with the last digit of your answer.", "If they differ, there's a slip."],
    examples: [
      { q: "47 × 6", answer: 282, walk: "7 × 6 = 42, so the answer must end in 2. 282 does." },
      { q: "38 + 57", answer: 95, walk: "8 + 7 = 15, so it must end in 5. 95 does." },
    ],
    when: "After you have an answer you're not sure of.",
    watch: "Checking the same way you calculated. Use a different route.",
    tip: "It doesn't prove you're right, but it catches most slips.",
    fixes: ["close", "transpose", "carry"],
  },
];

export const LESSON_KEYS = LESSONS.map((l) => l.key);
export const lessonByKey = (key: string): Lesson | undefined => LESSONS.find((l) => l.key === key);

/** The most relevant lesson for a skill at a level: the highest one at or below `level`, else the first for that skill. */
export function lessonFor(skill: SkillKey, level: number): Lesson | undefined {
  const mine = LESSONS.filter((l) => l.skill === skill).sort((a, b) => a.level - b.level);
  return [...mine].reverse().find((l) => l.level <= level) ?? mine[0];
}

/** Lessons worth doing next: unlearned, for a skill the player can see, at a level they're about to reach. */
export function recommendLessons(rating: (k: SkillKey) => number, unlocked: SkillKey[], learned: Set<string>, limit = 3): Lesson[] {
  return LESSONS.filter((l) => l.skill !== "general" && !learned.has(l.key) && unlocked.includes(l.skill) && l.level <= Math.floor(rating(l.skill)) + 1)
    .sort((a, b) => a.level - b.level).slice(0, limit);
}

/**
 * The best strategy for a kind of miss in a skill: lessons that list the miss in `fixes`, from that skill or the
 * general habits, unlearned first, then the closest to the player's level.
 */
export function strategyFor(miss: MissType, skill: SkillKey, level: number, learned: Set<string>): Lesson | undefined {
  const pool = LESSONS.filter((l) => l.fixes?.includes(miss) && (l.skill === skill || l.skill === "general"));
  if (!pool.length) return lessonFor(skill, level);
  const score = (l: Lesson) => (learned.has(l.key) ? 100 : 0) + (l.skill === skill ? 0 : 20) + Math.abs(l.level - (level + 1));
  return [...pool].sort((a, b) => score(a) - score(b))[0];
}
